import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"
import { isV2Api } from "./api-contract"
import type { RichTextDoc } from "./rich-text"
import type { FileAttachment } from "./attachments"
import type { MessageReactionTally } from "./message-view"
import { v2Api } from "./v2/client"
import type {
  V2Case,
  V2CaseComment,
  V2CaseCommentsPage,
  V2CasesPage,
} from "./v2/contracts"
import { listAllEventPeople } from "./v2/organizer"

// Support-ticket data layer for the Inbox slice. Extracted from apps/admin's
// inline InboxPage queries into reusable query-option factories. CRITICAL: the
// query keys here match admin byte-for-byte (`["resource", <table>, <filters>]`)
// so the vendored realtime layer (realtime.ts → cache.ts) surgically patches
// these caches on live CRUD events with no extra wiring.

export type SupportTicket = {
  id: string
  eventId: string
  guestUserId: string
  subject: string
  status: string
  closedAt: string | null
  closedById: string | null
  assigneeUserId: string | null
  priority: string | null
  guestLastReadAt: string | null
  teamLastReadAt: string | null
  createdAt: string
  modifiedAt: string | null
}

export type TicketComment = {
  id: string
  ticketId: string
  // NULL when a sessionless attendee authored the comment (guest path) — the
  // server sets `authorGuestId` instead. `authorNameAtSend` is always set.
  authorUserId: string | null
  authorGuestId?: string | null
  authorNameAtSend: string
  authorRoleAtSend: string | null
  body: string
  // Constrained Tiptap JSON projection of `body` (string-encoded). NULL =
  // render `body` as plain text. Authored by the rich composer; legacy
  // surfaces (admin/iOS/guest) fall back to `body`.
  richText: string | null
  kind: string
  visibility: string | null
  createdAt: string
  editedAt: string | null
  // Soft-delete (moderation): set by deleteTicketComment when an admin/owner or
  // the author removes the comment. The read endpoint returns the redacted row
  // so the unified row can render a "message removed" tombstone. Null = live.
  deletedAt?: string | null
  // JSON array of attachments (file/image), stored verbatim + returned on read.
  // Null when the comment has none. Parsed by parseMessageAttachments.
  attachments?: string | null
  // JSON array of link unfurl cards; written async (waitUntil) post-send and
  // re-broadcast as an `edited` frame. Raw JSON, rendered by LinkUnfurl. Null = none.
  unfurls?: string | null
  // Client-only optimistic placeholder; cleared when the server echo reconciles.
  pending?: boolean
  clientId?: string | null
}

export type Guest = {
  id: string
  linkedUserId: string | null
  nameAtInvite: string | null
  emailAtInvite: string | null
  displayName: string | null
}

export type MemberRow = {
  userId: string
  role?: string
  user?: { id: string; name: string | null; email: string | null }
}

type ListEnvelope<T> = {
  data: T[]
  meta?: { total?: number }
  pagination?: { hasMore?: boolean; count?: number; page?: number; pageSize?: number }
}
type TicketThreadEnvelope = {
  success: true
  data: TicketComment[]
  nextCursor: string | null
  hasMore: boolean
  teamLastReadAt: string | null
  guestLastReadAt: string | null
  // Grouped reactions (parentType "ticket") keyed by comment id, bundled by
  // list-ticket-thread so the panel seeds useReactions without a second fetch.
  reactions?: Record<string, MessageReactionTally[]>
}

export type InboxBundle = {
  success: true
  tickets: SupportTicket[]
  guests: Guest[]
  members: MemberRow[]
  invitations: unknown[]
  initialTicketId: string | null
  comments: TicketComment[]
}

// ── query keys (must match apps/admin) ──────────────────────────────────────
export const ticketsKey = (eventId: string) =>
  ["resource", "support-tickets", { eventId }] as const
export const ticketCommentsKey = (ticketId: string) =>
  ["resource", "ticket-comments", { ticketId }] as const
export const guestsLookupKey = (eventId: string) =>
  ["resource", "guests", { eventId, mode: "inbox-lookup" }] as const

// ── query options ───────────────────────────────────────────────────────────
export function ticketsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ticketsKey(eventId),
    queryFn: () => isV2Api
      ? v2Api<V2CasesPage>(`/api/v2/events/${encodeURIComponent(eventId)}/cases`, {
          searchParams: { limit: 200 },
        }).then((response) => ({ data: response.cases.map(v2CaseToSupportTicket) }))
      : api<ListEnvelope<SupportTicket>>("/api/v1/support-tickets", {
        searchParams: { limit: 200, eventId, sort: "modifiedAt", order: "desc" },
      }),
    select: (r) => r.data,
  })
}

export function ticketCommentsQueryOptions(eventId: string, ticketId: string) {
  return queryOptions({
    queryKey: ticketCommentsKey(ticketId),
    // list-ticket-thread returns newest-first (DESC). Reverse to ASC HERE so the
    // STORED cache is oldest→newest — the order every writer already assumes: the
    // optimistic append in ConversationPanel, the realtime "append" splice
    // (realtime.ts tryMessageInsertSplice), loadOlder's older-block prepend, and
    // the bundle seed. Reversing in `select` (read-time) instead left the cache
    // DESC, so an appended optimistic comment landed at the OLDEST slot and
    // rendered at the TOP, then "dropped" to the bottom on the next realtime
    // refetch. Reverse once, at storage time, and all paths agree.
    queryFn: async () => {
      const r = isV2Api
        ? await v2Api<V2CaseCommentsPage>(
            `/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(ticketId)}/comments`,
            { searchParams: { limit: 100 } },
          ).then(v2CaseCommentsToEnvelope)
        : await api<TicketThreadEnvelope>(
            `/api/v1/event/${eventId}/chat/list-ticket-thread`,
            { method: "POST", body: { ticketId, limit: 100 } },
          )
      return { ...r, data: r.data.slice().reverse() }
    },
  })
}

export function fetchTicketCommentsPage(
  eventId: string,
  ticketId: string,
  before: string | null,
  limit = 100,
) {
  if (isV2Api) {
    return v2Api<V2CaseCommentsPage>(
      `/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(ticketId)}/comments`,
      { searchParams: { limit, before } },
    ).then(v2CaseCommentsToEnvelope)
  }
  return api<TicketThreadEnvelope>(
    `/api/v1/event/${eventId}/chat/list-ticket-thread`,
    {
      method: "POST",
      body: {
        ticketId,
        limit,
        ...(before ? { before } : {}),
      },
    },
  )
}

export function guestsLookupQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: guestsLookupKey(eventId),
    queryFn: () => isV2Api
      ? listAllEventPeople(eventId).then((people) => ({
          data: people.map((person): Guest => ({
            id: person.id,
            linkedUserId: person.personId,
            nameAtInvite: person.displayName,
            emailAtInvite: person.email,
            displayName: person.displayName,
          })),
        }))
      : api<ListEnvelope<Guest>>("/api/v1/guests", {
        searchParams: { limit: 1000, eventId },
      }),
    select: (r) => r.data,
  })
}

// One round trip that seeds tickets + guests + the initial thread's comments
// into the SAME cache keys the lists read — so realtime keeps patching them.
export async function loadInboxBundle(
  qc: QueryClient,
  eventId: string,
  deepLinkTicketId?: string | null,
): Promise<InboxBundle> {
  const b = isV2Api
    ? await v2Api<{
        success: true
        cases: V2Case[]
        eventPeople: Array<{
          id: string
          personId: string
          displayName: string
          email: string | null
        }>
        staff: Array<{ personId: string; displayName: string; email: string | null; role: string }>
        initialCaseId: string | null
        comments: V2CaseComment[]
      }>(`/api/v2/events/${encodeURIComponent(eventId)}/cases/inbox`, {
        searchParams: { caseId: deepLinkTicketId },
      }).then((response): InboxBundle => ({
        success: true,
        tickets: response.cases.map(v2CaseToSupportTicket),
        guests: response.eventPeople.map((person) => ({
          id: person.id,
          linkedUserId: person.personId,
          nameAtInvite: person.displayName,
          emailAtInvite: person.email,
          displayName: person.displayName,
        })),
        members: response.staff.map((person) => ({
          userId: person.personId,
          role: person.role,
          user: {
            id: person.personId,
            name: person.displayName,
            email: person.email,
          },
        })),
        invitations: [],
        initialTicketId: response.initialCaseId,
        comments: response.comments.map(v2CaseCommentToTicketComment),
      }))
    : await api<InboxBundle>(`/api/v1/event/${eventId}/chat/get-inbox-admin`, {
        method: "POST",
        body: { ticketId: deepLinkTicketId ?? undefined },
      })
  qc.setQueryData<ListEnvelope<SupportTicket>>(ticketsKey(eventId), { data: b.tickets })
  qc.setQueryData<ListEnvelope<Guest>>(guestsLookupKey(eventId), { data: b.guests })
  if (b.initialTicketId && b.comments) {
    qc.setQueryData<TicketThreadEnvelope>(ticketCommentsKey(b.initialTicketId), {
      success: true,
      // get-inbox-admin returns these comments ASC (oldest→newest) already —
      // the same orientation the cache now stores (see ticketCommentsQueryOptions),
      // so seed them as-is. (Was `.reverse()` back when the cache was DESC.)
      data: b.comments.slice(),
      nextCursor: null,
      hasMore: false,
      teamLastReadAt: null,
      guestLastReadAt: null,
    })
  }
  return b
}

// ── mutation ─────────────────────────────────────────────────────────────────
export type PostCommentInput = {
  ticketId: string
  body: string
  // Public (guest + team) vs internal (team-only note). NOTE: the backend
  // action's input field is `visibility` — `kind` is a DB column ("text" /
  // "system"), NOT an input. We carry it as `kind` client-side (it drives the
  // optimistic bubble) but send it to the server as `visibility`.
  kind: "public" | "internal"
  clientId: string
  // Rich Tiptap doc; the server sanitizes + stores it as richText (body is the
  // flattened plain-text fallback). Optional — omit for plain text.
  richTextDoc?: RichTextDoc
  // Uploaded file attachments to stash on the comment (presigned-R2 mediaIds).
  attachments?: FileAttachment[]
}

export async function postTicketComment(
  eventId: string,
  input: PostCommentInput,
): Promise<{ comment: TicketComment }> {
  if (isV2Api) {
    const response = await v2Api<{ success: true; comment: V2CaseComment }>(
      `/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(input.ticketId)}/comments`,
      {
        method: "POST",
        body: {
          idempotencyKey: crypto.randomUUID(),
          body: input.body,
          visibility: input.kind,
          clientId: input.clientId,
          ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
          attachmentManifest: input.attachments ?? [],
        },
      },
    )
    return { comment: v2CaseCommentToTicketComment(response.comment) }
  }
  return api<{ comment: TicketComment }>(
    `/api/v1/event/${eventId}/chat/post-ticket-comment`,
    {
      method: "POST",
      body: {
        ticketId: input.ticketId,
        body: input.body,
        // Map client `kind` → server `visibility` (the action's actual field).
        visibility: input.kind,
        clientId: input.clientId,
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        ...(input.attachments && input.attachments.length > 0
          ? { attachments: input.attachments }
          : {}),
      },
    },
  )
}

// ── display helpers ──────────────────────────────────────────────────────────
export function guestNameMap(guests: Guest[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const g of guests) {
    const name = g.displayName ?? g.nameAtInvite ?? g.emailAtInvite ?? null
    if (g.linkedUserId && name) m.set(g.linkedUserId, name)
  }
  return m
}

export function memberNameMap(members: MemberRow[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const mem of members) {
    const id = mem.user?.id ?? mem.userId
    const name = mem.user?.name ?? mem.user?.email ?? null
    if (id && name) m.set(id, name)
  }
  return m
}

export type Priority = "urgent" | "high" | "normal" | "low"

export function priorityRank(p: string | null): number {
  switch (p) {
    case "urgent":
      return 0
    case "high":
      return 1
    case "low":
      return 3
    default:
      return 2
  }
}

export function isTicketOpen(t: SupportTicket): boolean {
  return !["resolved", "closed", "cancelled"].includes(t.status) && t.closedAt == null
}

// The team has unread material if the latest activity (modifiedAt — bumped on
// each new comment) is after teamLastReadAt. Missing read state never lights
// the dot — only an explicit "activity after read marker" does. Mirrors
// apps/admin's isUnread.
export function isTicketUnread(t: SupportTicket): boolean {
  if (!t.teamLastReadAt) return false
  const last = t.modifiedAt ?? t.createdAt
  return last > t.teamLastReadAt
}

// ── triage vocab (matches the backend status/priority enums) ────────────────
export type TicketStatus = "open" | "pending" | "on_hold" | "closed"
export const TICKET_STATUSES: TicketStatus[] = ["open", "pending", "on_hold", "closed"]
export const TICKET_PRIORITIES: Priority[] = ["urgent", "high", "normal", "low"]

export function statusLabel(s: string): string {
  switch (s) {
    case "open":
      return "Open"
    case "pending":
      return "Pending"
    case "on_hold":
      return "On hold"
    case "closed":
      return "Closed"
    default:
      return s ? s.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Open"
  }
}

export function priorityLabel(p: string | null | undefined): string {
  const v = p ?? "normal"
  return v.replace(/^./, (c) => c.toUpperCase())
}

// ── triage mutations (mirror apps/admin/InboxPage) ──────────────────────────
// Every action returns the updated ticket row; we splice it into the list
// cache (the same key the realtime layer patches) instead of refetching.
type TicketActionResult = {
  success: true
  ticket?: SupportTicket
  unchanged?: boolean
  alreadyClosed?: boolean
  alreadyOpen?: boolean
}

export async function assignSupportTicket(
  eventId: string,
  ticketId: string,
  assigneeUserId: string | null,
): Promise<TicketActionResult> {
  if (isV2Api) {
    const response = await v2Api<{ success: true; case: V2Case }>(
      `/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(ticketId)}/assignment`,
      {
        method: "PUT",
        body: {
          idempotencyKey: crypto.randomUUID(),
          assigneePersonId: assigneeUserId,
        },
      },
    )
    return { success: true, ticket: v2CaseToSupportTicket(response.case) }
  }
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/assign-support-ticket`, {
    method: "POST",
    body: { ticketId, assigneeUserId },
  })
}

export async function setSupportTicketPriority(
  eventId: string,
  ticketId: string,
  priority: Priority,
): Promise<TicketActionResult> {
  if (isV2Api) return patchV2Case(eventId, ticketId, { priority })
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/set-support-ticket-priority`, {
    method: "POST",
    body: { ticketId, priority },
  })
}

export async function setSupportTicketStatus(
  eventId: string,
  ticketId: string,
  status: TicketStatus,
): Promise<TicketActionResult> {
  if (isV2Api) return patchV2Case(eventId, ticketId, { status })
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/set-support-ticket-status`, {
    method: "POST",
    body: { ticketId, status },
  })
}

export async function closeSupportTicket(
  eventId: string,
  ticketId: string,
): Promise<TicketActionResult> {
  if (isV2Api) return patchV2Case(eventId, ticketId, { status: "closed" })
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/close-support-ticket`, {
    method: "POST",
    body: { ticketId },
  })
}

export async function reopenSupportTicket(
  eventId: string,
  ticketId: string,
): Promise<TicketActionResult> {
  if (isV2Api) return patchV2Case(eventId, ticketId, { status: "open" })
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/reopen-support-ticket`, {
    method: "POST",
    body: { ticketId },
  })
}

export async function markTicketRead(
  eventId: string,
  ticketId: string,
): Promise<{ success: true; side: "team" | "attendee"; lastReadAt: string }> {
  if (isV2Api) {
    return v2Api(`/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(ticketId)}/read`, {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID() },
    })
  }
  return api(`/api/v1/event/${eventId}/chat/mark-ticket-read`, {
    method: "POST",
    body: { ticketId },
  })
}

// Open a NEW ticket to one or many guests via the group-concierge action.
// One ticket, N participants (single recipient → a 1:1 ticket). Supports
// dryRun for the review step.
export type NewTicketResult = {
  success: true
  committed: boolean
  recipientCount: number
  recipientUserIds?: string[]
  ticketId?: string
  participantUserIds?: string[]
  tickets?: Array<{ ticketId: string; guestUserId: string }>
}

export async function sendNewTicket(input: {
  eventId: string
  userIds: string[]
  subject: string
  body: string
  dryRun: boolean
}): Promise<NewTicketResult> {
  if (isV2Api) {
    if (input.dryRun) {
      return {
        success: true,
        committed: false,
        recipientCount: input.userIds.length,
        recipientUserIds: input.userIds,
      }
    }
    const response = await v2Api<{ success: true; case: V2Case }>(
      `/api/v2/events/${encodeURIComponent(input.eventId)}/cases`,
      {
        method: "POST",
        body: {
          idempotencyKey: crypto.randomUUID(),
          subject: input.subject,
          body: input.body,
          participantPersonIds: input.userIds,
        },
      },
    )
    return {
      success: true,
      committed: true,
      recipientCount: input.userIds.length,
      recipientUserIds: input.userIds,
      ticketId: response.case.id,
      participantUserIds: input.userIds,
    }
  }
  return api<NewTicketResult>("/api/v1/chat/send-to-support-tickets", {
    method: "POST",
    body: {
      eventId: input.eventId,
      subject: input.subject,
      body: input.body,
      recipientRule: { kind: "user_ids", userIds: input.userIds },
      dryRun: input.dryRun,
    },
  })
}

async function patchV2Case(
  eventId: string,
  caseId: string,
  patch: { status?: TicketStatus; priority?: Priority },
): Promise<TicketActionResult> {
  const response = await v2Api<{ success: true; case: V2Case }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/cases/${encodeURIComponent(caseId)}`,
    { method: "PATCH", body: patch },
  )
  return { success: true, ticket: v2CaseToSupportTicket(response.case) }
}

export function v2CaseToSupportTicket(value: V2Case): SupportTicket {
  const participant = value.participants.find((person) => person.role === "participant")
  const status = value.status === "pending_participant"
    ? "pending"
    : value.status === "pending_staff"
      ? "on_hold"
      : value.status === "resolved" || value.status === "cancelled"
        ? "closed"
        : value.status
  return {
    id: value.id,
    eventId: value.eventId,
    guestUserId: participant?.personId ?? "",
    subject: value.subject,
    status,
    closedAt: value.closedAt,
    closedById: value.closedByPersonId,
    assigneeUserId: value.assigneePersonId,
    priority: value.priority,
    guestLastReadAt: value.participantLastReadAt,
    teamLastReadAt: value.teamLastReadAt,
    createdAt: value.createdAt,
    modifiedAt: value.modifiedAt,
  }
}

export function v2CaseCommentToTicketComment(value: V2CaseComment): TicketComment {
  return {
    id: value.id,
    ticketId: value.caseId,
    authorUserId: value.authorPersonId,
    authorGuestId: null,
    authorNameAtSend: value.authorDisplayName,
    authorRoleAtSend: value.authorRole,
    body: value.body,
    richText: value.richTextDoc ? JSON.stringify(value.richTextDoc) : null,
    kind: value.messageKind,
    visibility: value.visibility,
    createdAt: value.sentAt,
    editedAt: value.editedAt,
    deletedAt: value.deletedAt,
    attachments: value.attachmentManifest.length
      ? JSON.stringify(value.attachmentManifest)
      : null,
    clientId: value.clientId,
  }
}

function v2CaseCommentsToEnvelope(value: V2CaseCommentsPage): TicketThreadEnvelope {
  return {
    success: true,
    data: value.comments.map(v2CaseCommentToTicketComment),
    nextCursor: value.page.nextCursor,
    hasMore: value.page.hasMore,
    teamLastReadAt: value.case.teamLastReadAt,
    guestLastReadAt: value.case.participantLastReadAt,
    reactions: {},
  }
}

// Splice an updated ticket row into the list cache (the realtime-patched key).
// Returns true when the row was found + patched; false → caller should fall
// back to invalidating the list.
export function patchTicketInCache(
  qc: QueryClient,
  eventId: string,
  ticket: Partial<SupportTicket> & { id: string },
): boolean {
  let patched = false
  qc.setQueryData<ListEnvelope<SupportTicket>>(ticketsKey(eventId), (old) => {
    if (!old) return old
    const next = old.data.map((t) => {
      if (t.id !== ticket.id) return t
      patched = true
      return { ...t, ...ticket }
    })
    return patched ? { ...old, data: next } : old
  })
  return patched
}
